import { useApolloClient } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useDeleteFormForMe } from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { refreshFormAdmissionsAfterMutation } from "src/scenes/parent/formTemplates/admissions";
import { triggerRuleExecutionsForSoftDeletedOffers } from "src/services/form/offer";
import * as GQL from "src/types/graphql";
import { DELETE_FORM } from "../../formTemplates/graphql/mutations";
import {
  GET_PARENT_ASSIGNED_FORMS,
  GET_PARENT_FORMS_INDEX,
} from "../../formTemplates/graphql/queries";
import { useNextStep } from "../../nextStep/NextStepProvider";

export function useDeleteForm() {
  const flags = useFlags(["form-service-form-status"]);

  const gqlHook = useDeleteFormGQL();
  const restHook = useDeleteFormREST();

  if (flags["form-service-form-status"].enabled) {
    return restHook;
  } else {
    return gqlHook;
  }
}

function useDeleteFormGQL() {
  const [
    deleteForm,
    {
      remoteData: { isLoading },
    },
  ] = useRemoteDataMutation<GQL.DeleteForm, GQL.DeleteFormVariables>(
    DELETE_FORM
  );

  const deleteFormFn = React.useCallback(
    async (formId: string) => {
      const result = await deleteForm({
        variables: {
          form_id: formId,
        },
        refetchQueries: [GET_PARENT_FORMS_INDEX, GET_PARENT_ASSIGNED_FORMS],
      });

      try {
        await FSR.createRuleExecutionsForMe({
          body: {
            data: {
              targets: [{ type: "form", formId }],
            },
          },
        });
      } catch (error) {
        console.warn("Error executing form rules:", error);
      }
      await triggerRuleExecutionsForSoftDeletedOffers({
        offers: result.data?.delete_offer?.returning ?? [],
        scope: "me",
      });
    },
    [deleteForm]
  );

  return { isDeleting: isLoading(), deleteForm: deleteFormFn };
}

function useDeleteFormREST() {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useDeleteFormForMe();
  const nextStep = useNextStep();
  const deleteFormFn = React.useCallback(
    async (formId: string) => {
      const response = await mutateAsync({
        path: {
          formId: formId,
        },
      });
      nextStep.process(response.data?.data.assignedForms);
      await refreshFormAdmissionsAfterMutation(queryClient, formId);
      await apolloClient.refetchQueries({
        include: [GET_PARENT_FORMS_INDEX, GET_PARENT_ASSIGNED_FORMS],
      });
    },
    [apolloClient, mutateAsync, nextStep, queryClient]
  );

  return { isDeleting: isPending, deleteForm: deleteFormFn };
}
